'use client';
import * as React from 'react';
import { useContext, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { ChevronDown, LogOut } from 'lucide-react';
import { SignOutModal } from './sign-out-modal';
import { Button } from '@/src/components/ui/button';
import { SidebarContext } from './sidebar-client';

interface SidebarItemProps {
  label: string;
  icon?: React.ReactNode;
  href?: string;
  children?: React.ReactNode;
}

interface SidebarLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export function SidebarItem({ label, icon, href, children }: SidebarItemProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const { closeMenu } = useContext(SidebarContext);

  // Get all child hrefs for smarter active detection
  const childHrefs = React.Children.toArray(children)
    .filter((child): child is React.ReactElement<SidebarLinkProps> =>
      React.isValidElement(child)
    )
    .map((child) => child.props.href);

  // Check if a specific child href is active
  // Prefer longest match to avoid parent routes highlighting when on child routes
  const isChildHrefActive = (childHref: string) => {
    if (pathname === childHref) return true;
    if (pathname.startsWith(childHref + '/')) {
      // Check if any sibling has a longer/better match
      const hasLongerMatch = childHrefs.some(
        (otherHref) =>
          otherHref !== childHref &&
          pathname.startsWith(otherHref) &&
          otherHref.length > childHref.length
      );
      return !hasLongerMatch;
    }
    return false;
  };

  // Check if any child route is active
  const hasActiveChild = childHrefs.some((childHref) =>
    isChildHrefActive(childHref)
  );

  // If this item has a direct href, check if it's active
  const isActive = href
    ? pathname === href || pathname.startsWith(href + '/')
    : hasActiveChild;

  // Handle item click - for direct links, close the menu
  const handleItemClick = () => {
    if (href) {
      closeMenu();
    }
  };

  return (
    <li className='list-none'>
      {href ? (
        <Link
          href={href}
          className={`sidebar-link ${isActive ? 'active' : ''}`}
          onClick={handleItemClick}
        >
          {icon}
          <span>{label}</span>
        </Link>
      ) : (
        <>
          <Button
            onClick={() => setIsOpen(!isOpen)}
            variant='ghost'
            className={`sidebar-link w-full justify-between ${isActive ? 'active' : ''}`}
          >
            <div className='flex items-center gap-3'>
              {icon}
              <span>{label}</span>
            </div>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </Button>
          {isOpen && children && (
            <div className='ml-9 mt-1 space-y-1'>
              {React.Children.map(children, (child) => {
                if (React.isValidElement<SidebarLinkProps>(child)) {
                  const childHref = child.props.href;
                  const isChildActive = isChildHrefActive(childHref);

                  // Create a wrapped version of the child with onClick handler
                  return React.cloneElement(child, {
                    ...child.props,
                    onClick: () => closeMenu(),
                    className: `block px-4 py-2 text-base font-medium rounded-md transition-colors
                      ${isChildActive ? 'bg-sidebar-active text-primary-foreground' : 'hover:bg-sidebar-hover'}`
                  });
                }
                return null;
              })}
            </div>
          )}
        </>
      )}
    </li>
  );
}

export function SidebarLogoutButton() {
  const [showModal, setShowModal] = useState(false);
  const { closeMenu } = useContext(SidebarContext);

  const handleLogoutClick = () => {
    closeMenu();
    setShowModal(true);
  };

  return (
    <>
      <Button
        onClick={handleLogoutClick}
        variant='ghost'
        className='sidebar-link text-muted-foreground hover:text-foreground'
      >
        <LogOut size={18} />
        <span>Sign Out</span>
      </Button>
      <SignOutModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}
